/* eslint-disable indent */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { Modal, SingleSelect } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import {
  COVER_PHOTO,
  DOWNLOAD_VIDEO_EXTENSION,
  IMAGE,
  STOCK_CLIP,
  TEXT,
  TEXT_OVERLAY,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetScope, AssetStatus, PERMISSION_TYPE } from '@/enums';
import { AssetType } from '@/enums/AssetEnums';
import { getThumbnailByType, splitFileNameAndExtension } from '@/helpers/AssetHelper';
import { RemoveSpecialCharacters } from '@/helpers/NamingHelper';
import { showToast, ToastType } from '@/helpers/toast';
import { useMobileScreen } from '@/hooks';
import {
  CheckIcon,
  BlueDownloadIcon as DownloadIcon,
  DuplicateIcon,
  EditPenIcon,
  EyeIcon,
  PlayIcon,
  PremiumIcon,
  TrashBlackIcon,
  TrashIcon,
  UsedInTributeVideo
} from '@/resources';
import {
  useGetTributeVideoByAssetId,
  useGetTributeVideoQuery,
  useUpdateAssetName,
  useUpdateTributeVideoName
} from '@/services';
import { getTestId, TestIds } from '@/shared/test-ids';
import { Asset, AssetItemProps, ThemeProp, TributeVideoAsset } from '@/types';

export const AssetItem = ({
  asset,
  deleteSelectedAsset,
  setSelectedAsset,
  setShowAsset,
  canBeDeleted = true,
  showDownloadIcon,
  selectedAsset,
  setIsDeleteOpen,
  canBeEdited,
  editSelectedAsset,
  assetType,
  order,
  handleUpdateAssetOrder,
  isDraggable,
  canBeDragged,
  selectedAssets,
  multiselect,
  setEnableEdit,
  clipSelect = false,
  showDurationMultiplier = false,
  onChangeDurationMultiplier,
  tributeVideoAsset,
  canBeSelected = false,
  isTributeVideoAssetsPage = false,
  canBePreviewed = false,
  onPreviewClick,
  dataTestId,
  canBeDuplicated = false,
  onDuplicateAssetClick,
  isPremium = false,
  isPremiumLocked = false
}: AssetItemProps) => {
  const { hasPermission } = useContext(AuthContext);
  const [showDeleteIcon, setShowDeleteIcon] = useState<boolean>(false);
  const [showEditIcon, setShowEditIcon] = useState<boolean>(false);
  const [showPreviewIcon, setShowPreviewIcon] = useState<boolean>(false);
  const [showDuplicateIcon, setShowDuplicateIcon] = useState<boolean>(false);
  const [showPremiumIcon, setShowPremiumIcon] = useState<boolean>(false);
  const dropAssetSlotDiv = useRef<HTMLDivElement | null>(null);
  const isLeftSideOfTargetDropDiv = useRef<boolean>(false);
  const [t] = useTranslation('common');
  const isMobile = useMobileScreen();
  const ASSET_ITEM_DOUBLE_BORDER = [
    'rounded-lg',
    'border-5',
    'border-[#D6E4FF]',
    'shadow-[inset_0_0_0_1px_#D6E4FF,inset_0_0_0_6px_#284986]',
    'p-1',
    '!filter-pop-up-shadow',
    ' !border-2'
  ].join(' ');

  const BASE_ASSET_ITEM_CLASSNAMES = [
    'hover:filter-pop-up-shadow',
    'relative',
    'flex',
    'flex-col',
    'justify-center',
    'rounded',
    'outline',
    'outline-1',
    'outline-secondary-blue-gray',
    'hover:outline-2'
  ].join(' ');

  const [clickCount, setClickCount] = useState(0);
  const [isEditingName, setIsEditingName] = useState(false);
  const [openAssetDurationModal, setOpenAssetDurationModal] = useState<boolean>(false);
  const [assetDurationMultiplier, setAssetDurationMultiplier] = useState<number>(
    tributeVideoAsset?.durationMultiplier && tributeVideoAsset.durationMultiplier > 0
      ? tributeVideoAsset.durationMultiplier
      : 1
  );
  const isTributeVideoAsset = assetType === AssetType.TRIBUTE_VIDEO.toString() && 'status' in asset;
  const isAsset = assetType === TRIBUTE_VIDEO && !('status' in asset);
  const isTheme = (item?: Asset | ThemeProp | TributeVideoAsset): item is ThemeProp =>
    !!item && ('themeKey' in item || 'themeSource' in item);
  const getThemeKey = (theme: ThemeProp) =>
    theme.themeKey ??
    `${theme.id}-${theme.themeSource ?? (theme.isPublish === undefined ? 'custom' : 'public')}`;
  const isSelectedItem = isTheme(asset)
    ? isTheme(selectedAsset) && getThemeKey(asset) === getThemeKey(selectedAsset)
    : selectedAsset?.id === asset?.id;
  const isPublicAsset =
    'assetScope' in asset &&
    (asset as Asset).assetScope?.toString().toLowerCase() ===
      AssetScope[AssetScope.PUBLIC].toLowerCase();

  const canEditAssetName = () => {
    if (isPublicAsset) {
      return hasPermission(PERMISSION_TYPE.CREATE_PUBLIC_ASSET);
    }
    if (isTheme(asset)) {
      return false;
    }
    return true;
  };
  const originalName =
    (asset as TributeVideoAsset).asset?.assetName ??
    (asset as Asset).assetName ??
    (asset as ThemeProp).name;
  const tributeThemeCardTestId =
    isTributeVideoAssetsPage && isTheme(asset)
      ? getTestId(TestIds.tributeVideo.themeCard, asset.name)
      : undefined;

  const [editedFileName, setEditedFileName] = useState('');
  const [extension, setExtension] = useState('');

  const { data: tributeVideoData } = useGetTributeVideoQuery(
    Number(asset.id),
    undefined,
    isTributeVideoAsset
  );
  const tributeVideo = tributeVideoData?.data;

  const { refetch: fetchTributeVideoByAsset } = useGetTributeVideoByAssetId(asset.id);

  const { mutate: updateAssetName } = useUpdateAssetName(queryClient, {
    onSuccess: () => undefined,
    onError: () => showToast(ToastType.ERROR, t('assetNameUpdateError'))
  });

  const { mutate: updateTributeVideoName } = useUpdateTributeVideoName(queryClient);

  const getEditedFileName = () => {
    setIsEditingName(false);

    const trimmedFileName = editedFileName.trim();
    if (!trimmedFileName) {
      const { fileName: initialFileName } = splitFileNameAndExtension(originalName);
      setEditedFileName(initialFileName);
      return;
    }

    return trimmedFileName + extension;
  };

  const handleSaveAssetName = async () => {
    const newFullName = getEditedFileName();

    if (!newFullName || newFullName === originalName) {
      return;
    }

    if (isTributeVideoAsset) {
      updateTributeVideoName({
        id: tributeVideo.id,
        name: newFullName
      });
      return;
    }

    if (isAsset) {
      const response = await fetchTributeVideoByAsset();
      const tributeVideo = response?.data?.data;

      if (!tributeVideo) {
        throw new Error('Tribute video not found for the asset');
      }

      updateTributeVideoName({
        id: tributeVideo.id,
        name: newFullName
      });
      return;
    }

    updateAssetName({
      id: asset.id,
      assetName: newFullName
    });
  };

  const handleClickAsset = () => {
    setSelectedAsset?.(asset as Asset | ThemeProp);
    setShowAsset?.(true);
  };

  const handleDropSlot = (e: React.DragEvent<HTMLDivElement>) => {
    if (order && e.dataTransfer) {
      const dataTransfer = JSON.parse(e.dataTransfer.getData('application/slotJson'));
      let newOrder;

      switch (true) {
        case isLeftSideOfTargetDropDiv.current && dataTransfer.order > order:
          newOrder = order;
          break;
        case isLeftSideOfTargetDropDiv.current && dataTransfer.order < order:
          newOrder = order - 1;
          break;
        case !isLeftSideOfTargetDropDiv.current && dataTransfer.order < order:
          newOrder = order;
          break;
        case dataTransfer.order === order:
          newOrder = order;
          break;
        default:
          newOrder = order + 1;
          break;
      }
      if (dataTransfer.order !== newOrder) {
        handleUpdateAssetOrder?.(dataTransfer.order, newOrder as number);
      }
      if (dropAssetSlotDiv.current) {
        dropAssetSlotDiv.current.style.borderLeftColor = 'transparent';
        dropAssetSlotDiv.current.style.borderRightColor = 'transparent';
      }
    }
  };

  const statusColor = () => {
    if ((asset as Asset).status) {
      switch ((asset as Asset).status) {
        case AssetStatus.PREVIEW_CREATED:
          return 'bg-accent-green';
        case AssetStatus.CONTENT_PREPARING:
          return 'bg-accent-amber';
        case AssetStatus.CONTENT_PREPARED:
          return 'bg-secondary-steel-blue';
        case AssetStatus.COMPLETED:
          return 'bg-primary-blue';
        case AssetStatus.VIDEO_RENDERING:
        case AssetStatus.PREVIEW_RENDERING:
          return 'bg-secondary-gray';
        case AssetStatus.FAILED:
          return 'bg-accent-red';
        default:
          return 'bg-accent-green';
      }
    }
  };

  const handleStartDragSlot = (e: React.DragEvent<HTMLDivElement>) => {
    const dataTransfer = {
      id: asset.id,
      order,
      ...('assetType' in asset ? { assetType: asset.assetType } : {})
    };

    e.dataTransfer?.setData('application/slotJson', JSON.stringify(dataTransfer));
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropAssetSlotDiv.current) {
      dropAssetSlotDiv.current.style.borderLeftColor = 'transparent';
      dropAssetSlotDiv.current.style.borderRightColor = 'transparent';
    }
  };

  const handleDragOverSlot = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (dropAssetSlotDiv.current && e.target instanceof Element && isDraggable) {
      const cursorPositionInTarget = e.clientX - e.target.getBoundingClientRect().left;
      const dragToLeft = cursorPositionInTarget < e.target.clientWidth / 2;
      isLeftSideOfTargetDropDiv.current = dragToLeft;
      if (isLeftSideOfTargetDropDiv.current) {
        dropAssetSlotDiv.current.style.borderLeftColor = 'var(--primary-blue)';
        dropAssetSlotDiv.current.style.borderRightColor = 'transparent';
        dropAssetSlotDiv.current.style.borderLeftWidth = '2px';
      } else {
        dropAssetSlotDiv.current.style.borderLeftColor = 'transparent';
        dropAssetSlotDiv.current.style.borderRightColor = 'var(--primary-blue)';
        dropAssetSlotDiv.current.style.borderRightWidth = '2px';
      }
    }
  };

  const isCoverPhoto = (asset as Asset).assetType === COVER_PHOTO;
  const isTextOverlay = (asset as Asset).assetType === TEXT_OVERLAY;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropAssetSlotDiv.current && !dropAssetSlotDiv.current.contains(event.target as Node)) {
        setClickCount(0);
        setShowDeleteIcon(false);
        setShowEditIcon(false);
        setShowDuplicateIcon(false);
        setShowPremiumIcon(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener);
    };
  }, []);

  const handleMobileClick = () => {
    const clickCountLocal = clickCount + 1;
    if (clickCountLocal % 2 === 1) {
      canBeDeleted && setShowDeleteIcon(true);
      canBeEdited && setShowEditIcon(true);
      canBePreviewed && !isPremiumLocked && setShowPreviewIcon(true);
      canBeDuplicated && setShowDuplicateIcon(true);
      isPremium && setShowPremiumIcon(true);
    } else {
      handleClickAsset();
    }
    setClickCount(clickCountLocal);
  };

  const handleChangeDurationMultiplier = (newValue: number) => {
    const durationMultiplierValue = newValue > 0 ? newValue : 1;
    if (tributeVideoAsset) {
      tributeVideoAsset.durationMultiplier = durationMultiplierValue;
      onChangeDurationMultiplier?.(tributeVideoAsset.id, durationMultiplierValue);
    }
    setOpenAssetDurationModal(false);
  };

  const durationOptions = useMemo(() => {
    return [
      { value: 1, label: t('originalDuration') },
      { value: 2, label: t('doubleDuration') },
      { value: 3, label: t('tripleDuration') }
    ];
  }, [t]);

  useEffect(() => {
    if (openAssetDurationModal) {
      setAssetDurationMultiplier(
        tributeVideoAsset?.durationMultiplier && tributeVideoAsset.durationMultiplier > 0
          ? tributeVideoAsset.durationMultiplier
          : 1
      );
    }
  }, [openAssetDurationModal, tributeVideoAsset]);

  useEffect(() => {
    const originalName =
      (asset as TributeVideoAsset).asset?.assetName ??
      (asset as Asset).assetName ??
      (asset as ThemeProp).name;
    const { fileName: initialFileName, extension: fileExtension } =
      splitFileNameAndExtension(originalName);
    setEditedFileName(initialFileName);
    setExtension(fileExtension);
  }, [asset]);

  return (
    <div
      draggable={isDraggable}
      ref={dropAssetSlotDiv}
      onDragStart={handleStartDragSlot}
      onDragOver={handleDragOverSlot}
      onDragLeave={onDragLeave}
      onDrop={(e) => handleDropSlot(e)}
      className={`flex flex-col ${
        isDraggable ? 'px-3 pt-3' : 'gap-y-2.5'
      } border border-transparent`}>
      <div
        onMouseEnter={() => {
          !isMobile && canBeDeleted && setShowDeleteIcon(true);
          !isMobile && canBeEdited && setShowEditIcon(true);
          !isMobile && canBePreviewed && !isPremiumLocked && setShowPreviewIcon(true);
          !isMobile && canBeDuplicated && setShowDuplicateIcon(true);
          !isMobile && isPremium && setShowPremiumIcon(true);
        }}
        onMouseLeave={() => {
          !isMobile && setShowDeleteIcon(false);
          !isMobile && setShowEditIcon(false);
          !isMobile && setShowPreviewIcon(false);
          !isMobile && setShowDuplicateIcon(false);
          !isMobile && setShowPremiumIcon(false);
        }}
        className={clsx(
          BASE_ASSET_ITEM_CLASSNAMES,
          canBeDeleted && 'cursor-pointer',
          canBeDragged && 'cursor-grab',
          isSelectedItem && !multiselect && !clipSelect && ASSET_ITEM_DOUBLE_BORDER,
          isPremiumLocked && 'cursor-not-allowed opacity-50'
        )}
        data-testid={
          dataTestId ?? (isCoverPhoto ? TestIds.coverPhotos.assetItemCard : tributeThemeCardTestId)
        }
        onClick={isPremiumLocked ? undefined : isMobile ? handleMobileClick : handleClickAsset}>
        <img
          id={asset.id.toString()}
          data-type={(asset as Asset).assetType}
          src={getThumbnailByType(asset as Asset)}
          className={clsx(
            assetType === COVER_PHOTO || assetType === TEXT_OVERLAY
              ? 'object-contain'
              : 'aspect-square object-cover',
            canBeSelected && !isPremiumLocked && 'cursor-pointer',
            'rounded'
          )}
        />
        {isPremium && showPremiumIcon && (
          <PremiumIcon className='absolute left-0 top-0 ml-2 mt-2 size-7' />
        )}
        {clipSelect && (
          <div className='absolute right-2 top-2 flex size-6 cursor-pointer items-center justify-center rounded-full border border-solid border-secondary-steel-blue bg-primary-white'>
            {isSelectedItem && (
              <div className='size-4 items-center justify-center rounded-full bg-primary-blue'></div>
            )}
          </div>
        )}
        {((asset as Asset).assetType === IMAGE || (asset as Asset).assetType === VIDEO) &&
          ((asset as Asset).tributeVideos === undefined
            ? false
            : (asset as Asset).tributeVideos.length > 0) && (
            <div className='absolute bottom-0 left-0 mb-2 ml-2 text-xl'>
              <UsedInTributeVideo />
            </div>
          )}
        {((asset as Asset).assetType === VIDEO || (asset as Asset).assetType === STOCK_CLIP) &&
          (asset as ThemeProp | Asset).duration && (
            <div className='absolute left-0 top-0 ml-2 mt-2 rounded-md bg-white px-2 py-0.5 text-xs text-accent-dark-gray'>
              {new Date((asset as ThemeProp).duration * 1000).toISOString().substring(14, 19)}
            </div>
          )}
        {multiselect && (
          <div
            className={`absolute right-0 top-0 flex size-4 cursor-pointer items-center justify-center rounded ${
              selectedAssets?.some((selectedAsset) => selectedAsset.id === asset.id)
                ? 'bg-primary-blue'
                : 'border border-secondary-steel-blue bg-white'
            } mr-2 mt-2`}>
            <CheckIcon />
          </div>
        )}
        {(assetType === VIDEO ||
          (assetType === AssetType.TRIBUTE_VIDEO.toString() && (asset as Asset).thumbnailUrl)) && (
          <div>
            <div className='absolute left-0 top-0 m-[35%] size-11 rounded-full bg-primary-gray md:m-[40%]'>
              <PlayIcon className='ml-[27%] mt-[25%] text-2xl text-secondary-steel-blue' />
            </div>
          </div>
        )}
        <div
          className={`${
            assetType === AssetType.TRIBUTE_VIDEO.toString() ? statusColor() : 'hidden'
          } absolute bottom-3 left-2.5 z-10 flex cursor-pointer items-center justify-center rounded-md px-3 py-2 text-xs text-white`}>
          {t((asset as Asset)?.status as string)}
        </div>
        {showDuplicateIcon && (
          <div
            className={clsx(
              'absolute top-3 z-10 flex items-center justify-center',
              showEditIcon && showDeleteIcon
                ? 'right-[78px]'
                : showEditIcon || showDeleteIcon
                  ? 'right-11'
                  : 'right-2.5'
            )}>
            <div
              className='flex size-7 cursor-pointer items-center justify-center rounded-md bg-primary-white hover:bg-white md:bg-white/[0.6]'
              onClick={(e) => {
                e.stopPropagation();
                onDuplicateAssetClick?.(asset as Asset);
              }}>
              <DuplicateIcon className='text-primary-blue' />
            </div>
          </div>
        )}
        {showEditIcon && (
          <div
            className={clsx(
              'absolute top-3 z-10 flex items-center justify-center',
              showDeleteIcon ? 'right-11' : 'right-2.5'
            )}>
            <div
              data-testid={
                isTributeVideoAssetsPage
                  ? TestIds.tributeVideo.customThemeEditButton
                  : isTextOverlay
                    ? getTestId(TestIds.eventTextOverlay.editButton, asset.id.toString())
                    : TestIds.uploadContent.assetItemEditButton
              }
              className={`flex size-7 cursor-pointer items-center justify-center rounded-md ${isTributeVideoAssetsPage ? 'bg-blue-powder-light hover:bg-blue-powder' : 'bg-primary-white hover:bg-white md:bg-white/[0.6]'}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAsset?.(asset as Asset | ThemeProp);
                editSelectedAsset?.(asset as Asset);
                setEnableEdit?.(true);
                assetType !== TEXT && setShowAsset?.(true);
                isTributeVideoAssetsPage && setOpenAssetDurationModal(true);
              }}>
              <EditPenIcon
                className={`${isTributeVideoAssetsPage ? 'text-primary-black' : 'text-primary-blue'}`}
              />
            </div>
            {((showDownloadIcon?.includes(asset as TributeVideoAsset) &&
              (asset as Asset).tributeVideoUrl) ||
              isCoverPhoto ||
              (isTextOverlay && !(asset as Asset).organizationId)) && (
              <div
                className='ml-[6.5px] flex size-7 items-center justify-center rounded-md bg-white/[.6] hover:bg-primary-white'
                onClick={(e) => {
                  e.stopPropagation();
                }}>
                <a
                  href={
                    isCoverPhoto || isTextOverlay
                      ? (asset as Asset).assetUrl
                      : (asset as Asset).tributeVideoUrl
                  }
                  download={
                    isCoverPhoto || isTextOverlay
                      ? (asset as Asset).assetName
                      : (asset as ThemeProp).name + DOWNLOAD_VIDEO_EXTENSION
                  }>
                  <DownloadIcon />
                </a>
              </div>
            )}
          </div>
        )}
        {showPreviewIcon && (
          <div
            data-testid={
              isTextOverlay
                ? getTestId(TestIds.eventTextOverlay.previewButton, asset.id.toString())
                : undefined
            }
            className={`absolute right-2.5 top-3 z-10 ml-1 flex size-7 cursor-pointer items-center justify-center rounded-md ${isTributeVideoAssetsPage ? 'bg-blue-powder hover:bg-blue-powder' : 'bg-primary-white hover:bg-white md:bg-white/[0.6]'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isPremiumLocked) {
                onPreviewClick?.();
              }
            }}>
            <EyeIcon
              className={isTributeVideoAssetsPage ? 'text-primary-black' : 'text-primary-blue'}
            />
          </div>
        )}
        {showDurationMultiplier &&
          tributeVideoAsset &&
          tributeVideoAsset?.durationMultiplier > 1 && (
            <div className='absolute left-1 top-3 ml-1 flex size-7 cursor-default items-center justify-center rounded bg-blue-powder'>
              <Label
                text={`${tributeVideoAsset.durationMultiplier.toString()}x`}
                type={LABEL_TYPE.PARAGRAPH2}
              />
            </div>
          )}
        {showDeleteIcon && (
          <div
            data-testid={
              isTextOverlay
                ? getTestId(TestIds.eventTextOverlay.deleteButton, asset.id.toString())
                : undefined
            }
            className={`absolute right-2.5 top-3 ml-1 flex size-7 cursor-pointer items-center justify-center rounded-md ${isTributeVideoAssetsPage ? 'bg-blue-powder-light hover:bg-blue-powder' : 'bg-primary-white hover:bg-white md:bg-white/[0.6]'}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedAsset?.(asset as Asset);
              deleteSelectedAsset?.(asset as Asset);
              setIsDeleteOpen?.(true);
            }}>
            {isTributeVideoAssetsPage ? <TrashBlackIcon /> : <TrashIcon />}
          </div>
        )}
      </div>
      {isEditingName && (
        <input
          value={editedFileName}
          onChange={(e) => {
            const filtered = RemoveSpecialCharacters(e.target.value);
            setEditedFileName(filtered);
          }}
          onPaste={(e) => {
            e.preventDefault();
            const paste = e.clipboardData.getData('text');
            const filtered = RemoveSpecialCharacters(paste);
            setEditedFileName((prev) => prev + filtered);
          }}
          onBlur={handleSaveAssetName}
          onKeyDown={(e) => e.key === 'Enter' && handleSaveAssetName()}
          className='w-full rounded-sm border border-primary-blue bg-primary-blue-10 px-1 py-0.5 text-xs text-accent-dark-gray outline-none'
          autoFocus
        />
      )}
      {!isEditingName && (
        <div
          className={clsx(
            'truncate px-1 py-0.5 text-xs text-accent-dark-gray',
            canEditAssetName() ? 'cursor-text' : 'cursor-default'
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (canEditAssetName()) {
              setIsEditingName(true);
            }
          }}>
          {RemoveSpecialCharacters(editedFileName + extension)}
        </div>
      )}
      <Modal
        isOpen={openAssetDurationModal}
        onClose={() => setOpenAssetDurationModal(false)}
        label={t('durationMultiplier')}
        modalClasses='w-full h-auto max-w-[360px]'
        hasCancelButton
        hasButton={false}
        buttonText={t('save')}
        buttonClasses='flex justify-between mb-0 !mr-0 flex'
        containerClasses='no-scrollbar mt-2 !overflow-y-visible'
        height='h-auto'
        handleSubmit={() => {
          handleChangeDurationMultiplier(assetDurationMultiplier);
          setOpenAssetDurationModal(false);
        }}>
        <SingleSelect
          id='durationMultiplierSelect'
          options={durationOptions}
          label={t('multiply')}
          handleChange={(option) => setAssetDurationMultiplier(option?.value as number)}
          value={durationOptions.find((opt) => opt.value === assetDurationMultiplier)}
        />
        <div className='mt-4 flex gap-4'>
          <Button
            type='submit'
            variant={ButtonVariant.QUATERNARY}
            handleOnClick={() => setOpenAssetDurationModal(false)}
            className='w-full px-3 !text-base'>
            {t('cancel')}
          </Button>
          <Button
            type='submit'
            variant={ButtonVariant.PRIMARY}
            handleOnClick={() => {
              handleChangeDurationMultiplier(assetDurationMultiplier);
              setOpenAssetDurationModal(false);
            }}
            className={'w-full px-3 !text-base'}>
            {t('save')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
